import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { useLegalDocuments, LegalDocument } from '@/hooks/useLegalDocuments';
import { 
  FileText, 
  Plus, 
  Edit, 
  Archive, 
  Send, 
  Trash2, 
  Loader2,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';

export default function AdminLegalDocumentsPage() {
  const navigate = useNavigate();
  const {
    documents,
    publishedDocument,
    isLoading,
    error,
    createDocument,
    updateDocument,
    publishDocument,
    archiveDocument,
    deleteDocument,
    isCreating,
    isUpdating,
    isPublishing,
  } = useLegalDocuments('producer_terms');

  const [newVersionDialog, setNewVersionDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<LegalDocument | null>(null);
  const [publishConfirm, setPublishConfirm] = useState<LegalDocument | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<LegalDocument | null>(null);

  // Form state for new version
  const [newVersion, setNewVersion] = useState('');
  const [newEffectiveFrom, setNewEffectiveFrom] = useState('');

  // Form state for editing
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editEffectiveFrom, setEditEffectiveFrom] = useState('');

  // Initialize edit form when dialog opens
  useEffect(() => {
    if (editDialog) {
      setEditTitle(editDialog.title);
      setEditContent(editDialog.content_md);
      setEditEffectiveFrom(editDialog.effective_from || '');
    }
  }, [editDialog]);

  const handleCreateNewVersion = async () => {
    if (!newVersion.trim()) {
      toast({ title: 'Ange versionsnummer', variant: 'destructive' });
      return;
    }

    try {
      // Copy content from published version if available
      const baseContent = publishedDocument?.content_md || '# Värdvillkor för Farmly\n\n...';
      
      await createDocument({
        doc_type: 'producer_terms',
        version: newVersion.trim(),
        title: 'Värdvillkor för Farmly',
        content_md: baseContent,
        effective_from: newEffectiveFrom || null,
      });

      toast({ title: 'Ny version skapad', description: `Version ${newVersion} skapad som utkast.` });
      setNewVersionDialog(false);
      setNewVersion('');
      setNewEffectiveFrom('');
    } catch (err) {
      toast({ 
        title: 'Kunde inte skapa version', 
        description: (err as Error).message,
        variant: 'destructive' 
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!editDialog) return;

    try {
      await updateDocument({
        id: editDialog.id,
        title: editTitle,
        content_md: editContent,
        effective_from: editEffectiveFrom || null,
      });

      toast({ title: 'Ändringar sparade' });
      setEditDialog(null);
    } catch (err) {
      toast({ 
        title: 'Kunde inte spara', 
        description: (err as Error).message,
        variant: 'destructive' 
      });
    }
  };

  const handlePublish = async () => {
    if (!publishConfirm) return;

    try {
      await publishDocument({ id: publishConfirm.id, archivePrevious: true });
      toast({ 
        title: 'Version publicerad', 
        description: `Version ${publishConfirm.version} är nu aktiv. Alla värdar behöver acceptera den nya versionen.`
      });
      setPublishConfirm(null);
    } catch (err) {
      toast({ 
        title: 'Kunde inte publicera', 
        description: (err as Error).message,
        variant: 'destructive' 
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteDocument(deleteConfirm.id);
      toast({ title: 'Utkast borttaget' });
      setDeleteConfirm(null);
    } catch (err) {
      toast({ 
        title: 'Kunde inte ta bort', 
        description: (err as Error).message,
        variant: 'destructive' 
      });
    }
  };

  const handleArchive = async (doc: LegalDocument) => {
    try {
      await archiveDocument(doc.id);
      toast({ title: 'Version arkiverad' });
    } catch (err) {
      toast({ 
        title: 'Kunde inte arkivera', 
        description: (err as Error).message,
        variant: 'destructive' 
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle2 className="h-3 w-3 mr-1" />Publicerad</Badge>;
      case 'draft':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Utkast</Badge>;
      case 'archived':
        return <Badge variant="outline"><Archive className="h-3 w-3 mr-1" />Arkiverad</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl py-8 px-4 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container max-w-4xl py-8 px-4">
        <Card className="border-destructive">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div>
              <p className="font-medium">Kunde inte ladda dokument</p>
              <p className="text-sm text-muted-foreground">{(error as Error).message}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl py-8 px-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-medium">Värdvillkor</h1>
          <p className="text-muted-foreground">Hantera versioner av värdvillkoren</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <a href="/legal/producer-terms" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Visa publik sida
            </a>
          </Button>
          <Button onClick={() => setNewVersionDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Ny version
          </Button>
        </div>
      </div>

      {/* Published version highlight */}
      {publishedDocument && (
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-700" />
                </div>
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    Aktiv version: {publishedDocument.version}
                    {getStatusBadge('published')}
                  </CardTitle>
                  <CardDescription>
                    {publishedDocument.effective_from && (
                      <>Gäller från: {format(new Date(publishedDocument.effective_from), 'd MMMM yyyy', { locale: sv })} • </>
                    )}
                    Publicerad: {format(new Date(publishedDocument.published_at!), 'd MMMM yyyy HH:mm', { locale: sv })}
                  </CardDescription>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditDialog(publishedDocument)}>
                <Edit className="h-4 w-4 mr-2" />
                Redigera
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}

      {!publishedDocument && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertCircle className="h-8 w-8 text-amber-600" />
            <div>
              <p className="font-medium">Ingen publicerad version</p>
              <p className="text-sm text-muted-foreground">
                Skapa och publicera en version för att aktivera producentvillkor.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Separator />

      {/* All versions list */}
      <div>
        <h2 className="text-lg font-medium mb-4">Alla versioner</h2>
        <div className="space-y-3">
          {documents?.map((doc) => (
            <Card key={doc.id} className={doc.status === 'published' ? 'border-green-200' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Version {doc.version}</span>
                        {getStatusBadge(doc.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Uppdaterad: {format(new Date(doc.updated_at), 'd MMM yyyy HH:mm', { locale: sv })}
                        {doc.effective_from && (
                          <> • Gäller från: {format(new Date(doc.effective_from), 'd MMM yyyy', { locale: sv })}</>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditDialog(doc)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    {doc.status === 'draft' && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setPublishConfirm(doc)}>
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(doc)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {doc.status === 'published' && (
                      <Button variant="ghost" size="sm" onClick={() => handleArchive(doc)}>
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {documents?.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Inga versioner ännu. Skapa en ny version för att komma igång.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* New Version Dialog */}
      <Dialog open={newVersionDialog} onOpenChange={setNewVersionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Skapa ny version</DialogTitle>
            <DialogDescription>
              {publishedDocument 
                ? `Ny version baseras på version ${publishedDocument.version}.`
                : 'Skapa första versionen av värdvillkoren.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="version">Versionsnummer</Label>
              <Input
                id="version"
                placeholder="t.ex. 1.1"
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="effective-from">Gäller från (valfritt)</Label>
              <Input
                id="effective-from"
                type="date"
                value={newEffectiveFrom}
                onChange={(e) => setNewEffectiveFrom(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewVersionDialog(false)}>
              Avbryt
            </Button>
            <Button onClick={handleCreateNewVersion} disabled={isCreating}>
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Skapa utkast
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(open) => !open && setEditDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Redigera version {editDialog?.version}</DialogTitle>
            <DialogDescription>
              Redigera villkorstexten i Markdown-format.
              {editDialog?.status === 'published' && (
                <span className="text-amber-600 block mt-1">
                  Obs: Ändringar i en publicerad version påverkar alla producenter.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Titel</Label>
              <Input
                id="edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-effective">Gäller från</Label>
              <Input
                id="edit-effective"
                type="date"
                value={editEffectiveFrom}
                onChange={(e) => setEditEffectiveFrom(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-content">Innehåll (Markdown)</Label>
              <Textarea
                id="edit-content"
                className="min-h-[400px] font-mono text-sm"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>
              Avbryt
            </Button>
            <Button onClick={handleSaveEdit} disabled={isUpdating}>
              {isUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Spara ändringar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation */}
      <AlertDialog open={!!publishConfirm} onOpenChange={(open) => !open && setPublishConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publicera version {publishConfirm?.version}?</AlertDialogTitle>
            <AlertDialogDescription>
              När du publicerar denna version kommer den att bli den aktiva versionen. 
              Alla producenter som har accepterat en tidigare version behöver acceptera den nya.
              {publishedDocument && publishedDocument.id !== publishConfirm?.id && (
                <span className="block mt-2 font-medium">
                  Version {publishedDocument.version} kommer att arkiveras.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handlePublish} disabled={isPublishing}>
              {isPublishing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Publicera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort utkast?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort version {deleteConfirm?.version}? Detta kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
